import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { usePhaseTree, pickDefaultPhase } from './usePhaseTree';
import { PhaseFeatureTree } from './PhaseFeatureTree';
import { AcceptanceFeatureDetail } from './FeatureDetail';
import { Can } from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { adminFeatureApi } from '../../api/adminApi';
import { featureApi } from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { OnboardingTour } from '../../components/OnboardingTour';
import { FeatureFormDialog } from '../Admin/FeatureFormDialog';
import { FeatureImportDialog } from '../Admin/FeatureImportDialog';
import { FeatureImagesDialog } from '../Admin/FeatureImagesDialog';
import DescriptionIcon from '@mui/icons-material/Description';
import { ConfirmDialog } from '../Admin/ConfirmDialog';
import type { FeatureWithStatus } from '../../types/acceptance';

interface AcceptanceScreenProps {
  projectId: string;
}

// Khoảng cách từ đỉnh viewport đến khối danh sách tính năng (AppBar cố định
// + padding của vùng nội dung), dùng để ghim (sticky) khối này khi cuộn trang.
const SIDEBAR_TOP_OFFSET = 88;

// Giới hạn & mặc định cho bề rộng cột danh sách tính năng (px) khi người dùng
// kéo thanh chia giữa hai cột. Bề rộng được nhớ lại qua localStorage.
const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 640;
const SIDEBAR_DEFAULT_WIDTH = 320;
const SIDEBAR_WIDTH_STORAGE_KEY = 'acceptance.sidebarWidth';

const clampSidebarWidth = (value: number): number =>
  Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(value)));

function readStoredSidebarWidth(): number {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? clampSidebarWidth(parsed) : SIDEBAR_DEFAULT_WIDTH;
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

/**
 * Màn hình nghiệm thu khách hàng cho một dự án: cây Phase → Feature bên trái,
 * chi tiết + hành động nghiệm thu bên phải. Hỗ trợ mở trực tiếp tới một
 * phase/feature qua query string (?phaseId=&featureId=) — dùng bởi PhaseDetail
 * và FeatureDetail (trang) khi điều hướng vào.
 *
 * Với người dùng có quyền quản lý tính năng (FEATURE_CREATE/UPDATE/DELETE),
 * màn này còn cho phép Thêm / Import / Sửa / Xoá tính năng ngay tại chỗ, tác
 * động vào giai đoạn đang chọn. Các nút đều bọc <Can> nên khách hàng không thấy.
 */
export function AcceptanceScreen({ projectId }: AcceptanceScreenProps): JSX.Element {
  const {
    phases,
    featuresByPhase,
    loadingPhases,
    loadingFeatures,
    errorByPhase,
    ensurePhaseLoaded,
    refreshPhase,
  } = usePhaseTree(projectId);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Trên mobile, danh sách và chi tiết xếp chồng theo cột dọc nên cuộn về đỉnh
  // trang chỉ đưa danh sách vào khung nhìn. Dùng ref này để cuộn thẳng tới
  // khối chi tiết khi người dùng chọn một tính năng.
  const detailSectionRef = useRef<HTMLDivElement | null>(null);
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const [resizing, setResizing] = useState(false);

  // Kéo thanh chia: theo dõi con trỏ ở mức document để không mất sự kiện khi
  // chuột đi ra ngoài thanh kéo, và khoá chọn văn bản trong lúc kéo.
  useEffect(() => {
    if (!resizing) return undefined;
    const handleMove = (event: PointerEvent) => {
      const left = layoutRef.current?.getBoundingClientRect().left ?? 0;
      setSidebarWidth(clampSidebarWidth(event.clientX - left));
    };
    const stop = () => setResizing(false);
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', stop);
    document.addEventListener('pointercancel', stop);
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', stop);
      document.removeEventListener('pointercancel', stop);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [resizing]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    } catch {
      /* bỏ qua khi localStorage không khả dụng */
    }
  }, [sidebarWidth]);

  // Bàn phím: hỗ trợ kéo bằng mũi tên trái/phải cho người dùng không dùng chuột.
  const handleResizerKeyDown = useCallback((event: ReactKeyboardEvent) => {
    const step = event.shiftKey ? 40 : 10;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setSidebarWidth((w) => clampSidebarWidth(w - step));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setSidebarWidth((w) => clampSidebarWidth(w + step));
    }
  }, []);
  const [openPhaseIds, setOpenPhaseIds] = useState<string[]>([]);
  const [defaultApplied, setDefaultApplied] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFeature, setSelectedFeature] = useState<FeatureWithStatus | null>(null);

  // State cho các thao tác quản lý (thêm/sửa/import/xoá).
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [importOpen, setImportOpen] = useState(false);
  const [imagesFeature, setImagesFeature] = useState<FeatureWithStatus | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FeatureWithStatus | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Tăng khi ảnh của feature đang xem thay đổi (upload/xoá trong dialog) để
  // khối chi tiết bên phải âm thầm tải lại ảnh, không cần đóng dialog mới thấy.
  const [imagesRefreshToken, setImagesRefreshToken] = useState(0);

  // Đánh dấu hoàn thành hàng loạt — chỉ Admin (FEATURE_CONFIRM_BULK) thấy checkbox.
  // Đây là đổi feature.status = COMPLETED, KHÔNG phải nghiệm thu (acceptance_status).
  const canBulkComplete = usePermission('FEATURE_CONFIRM_BULK');
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCompleting, setBulkCompleting] = useState(false);

  const initialFeatureId = searchParams.get('featureId');

  // Đổi dự án: trả mọi lựa chọn về mặc định. Cần thiết vì React Router giữ
  // nguyên component khi chỉ đổi :id trên URL — không có bước này thì cờ
  // defaultApplied vẫn bật (không giai đoạn nào được mở sẵn) và khung chi
  // tiết vẫn hiển thị tính năng của dự án cũ.
  useEffect(() => {
    setOpenPhaseIds([]);
    setDefaultApplied(false);
    setSelectedFeature(null);
  }, [projectId]);

  // Mở sẵn giai đoạn có order_index cao nhất (hoà thì mới nhất), đúng một lần
  // sau khi danh sách giai đoạn về. Cờ defaultApplied ngăn việc ép mở lại mỗi
  // khi dữ liệu đổi, vốn sẽ chống lại thao tác đóng của người dùng.
  useEffect(() => {
    if (defaultApplied || loadingPhases || phases.length === 0) return;
    const initialPhaseId = searchParams.get('phaseId');
    const target =
      (initialPhaseId ? phases.find((p) => p.id === initialPhaseId) : undefined) ??
      pickDefaultPhase(phases);
    if (target) {
      setOpenPhaseIds([target.id]);
      void ensurePhaseLoaded(target.id);
    }
    setDefaultApplied(true);
  }, [defaultApplied, loadingPhases, phases, searchParams, ensurePhaseLoaded]);

  const handleTogglePhase = useCallback(
    (phaseId: string) => {
      setOpenPhaseIds((prev) =>
        prev.includes(phaseId) ? prev.filter((id) => id !== phaseId) : [...prev, phaseId],
      );
      // Chỉ tải khi mở ra, và ensurePhaseLoaded tự bỏ qua nếu đã có sẵn.
      if (!openPhaseIds.includes(phaseId)) {
        void ensurePhaseLoaded(phaseId);
      }
    },
    [openPhaseIds, ensurePhaseLoaded],
  );

  // Chọn feature theo query string nếu có, ngược lại chọn feature đầu tiên
  // trong các giai đoạn ĐÃ TẢI. Không còn duyệt toàn dự án vì phần lớn giai
  // đoạn có thể chưa được tải.
  useEffect(() => {
    const loadedFeatures = openPhaseIds.flatMap((id) => featuresByPhase[id] ?? []);
    if (loadedFeatures.length === 0) return;
    // Đã có lựa chọn thì GIỮ NGUYÊN. Không kiểm tra nó có nằm trong các giai
    // đoạn đang mở hay không: chọn từ kết quả tìm kiếm toàn dự án là hợp lệ,
    // mà tính năng đó thường thuộc giai đoạn chưa mở — nếu bắt buộc phải nằm
    // trong danh sách đang mở thì lần featuresByPhase đổi kế tiếp sẽ âm thầm
    // nhảy về loadedFeatures[0], làm khung chi tiết văng đi ngay sau khi
    // người dùng vừa xác nhận nghiệm thu.
    if (selectedFeature) return;
    const target =
      (initialFeatureId ? loadedFeatures.find((f) => f.id === initialFeatureId) : undefined) ??
      loadedFeatures[0] ??
      null;
    setSelectedFeature(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPhaseIds, featuresByPhase]);

  const handleSelectFeature = useCallback(
    (feature: FeatureWithStatus) => {
      setSelectedFeature(feature);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('featureId', feature.id);
        next.set('phaseId', feature.phase_id);
        return next;
      });
      // Mobile: danh sách và chi tiết xếp chồng dọc nên cuộn thẳng tới khối
      // chi tiết. Desktop: hai cột nằm cạnh nhau, cuộn về đỉnh trang là đủ.
      if (isMobile) {
        detailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [setSearchParams, isMobile],
  );

  const currentPhaseId = useMemo(() => selectedFeature?.phase_id ?? '', [selectedFeature]);
  const currentPhaseName = useMemo(
    () => phases.find((p) => p.id === currentPhaseId)?.name ?? '',
    [phases, currentPhaseId],
  );

  const handleStatusChanged = useCallback(() => {
    void refreshPhase(currentPhaseId);
  }, [refreshPhase, currentPhaseId]);

  // Giai đoạn đích cho Thêm/Import: theo feature đang chọn, ngược lại là giai
  // đoạn đang mở đầu tiên.
  const managePhaseId = selectedFeature?.phase_id ?? openPhaseIds[0] ?? '';
  const managePhaseName = phases.find((p) => p.id === managePhaseId)?.name ?? '';

  const openCreate = () => {
    setFormMode('create');
    setImportOpen(false);
    setFormOpen(true);
  };

  const openEdit = () => {
    if (!selectedFeature) return;
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminFeatureApi.remove(deleteTarget.id);
      notify('Đã xóa mềm tính năng', 'success');
      setDeleteTarget(null);
      setSelectedFeature(null);
      await refreshPhase(deleteTarget.phase_id);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa tính năng'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleBulkSelect = useCallback((feature: FeatureWithStatus) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(feature.id)) next.delete(feature.id);
      else next.add(feature.id);
      return next;
    });
  }, []);

  const handleBulkComplete = async () => {
    setBulkCompleting(true);
    try {
      const { updated } = await featureApi.bulkComplete(projectId, [...bulkSelectedIds]);
      notify(`Đã đánh dấu hoàn thành ${updated.length} tính năng`, 'success');
      setBulkSelectedIds(new Set());
      setBulkDialogOpen(false);
      // Chỉ các giai đoạn đang mở mới có checkbox nên chỉ cần refresh chúng.
      await Promise.all(openPhaseIds.map((id) => refreshPhase(id)));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể đánh dấu hoàn thành hàng loạt'), 'error');
    } finally {
      setBulkCompleting(false);
    }
  };

  return (
    <Box
      ref={layoutRef}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'flex-start',
        gap: { xs: 2.5, md: 0 },
      }}
    >
      <OnboardingTour ready={!loadingPhases && selectedFeature !== null} />
      <Box
        sx={{
          width: { xs: '100%', md: sidebarWidth },
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Paper
          data-tour="feature-tree"
          variant="outlined"
          sx={{
            p: 2,
            position: { md: 'sticky' },
            top: { md: SIDEBAR_TOP_OFFSET },
            maxHeight: { md: `calc(100vh - ${SIDEBAR_TOP_OFFSET}px - 16px)` },
            overflowY: { md: 'auto' },
            overscrollBehavior: 'contain',
          }}
        >
          <Can permission="FEATURE_CREATE">
            <Stack spacing={1} sx={{ mb: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  disabled={!managePhaseId}
                  fullWidth
                >
                  Thêm
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setImportOpen(true)}
                  disabled={!managePhaseId}
                  fullWidth
                >
                  Import
                </Button>
              </Stack>
              {managePhaseName && (
                <Typography variant="caption" color="text.secondary">
                  Thêm/Import vào giai đoạn: <b>{managePhaseName}</b>
                </Typography>
              )}
            </Stack>
          </Can>
          {canBulkComplete && bulkSelectedIds.size > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Đã chọn {bulkSelectedIds.size} tính năng
              </Typography>
              <Button size="small" onClick={() => setBulkSelectedIds(new Set())}>
                Bỏ chọn
              </Button>
              <Button size="small" variant="contained" onClick={() => setBulkDialogOpen(true)}>
                Đánh dấu hoàn thành
              </Button>
            </Stack>
          )}
          <PhaseFeatureTree
            projectId={projectId}
            phases={phases}
            featuresByPhase={featuresByPhase}
            loadingPhases={loadingPhases}
            loadingFeatures={loadingFeatures}
            errorByPhase={errorByPhase}
            openPhaseIds={openPhaseIds}
            onTogglePhase={handleTogglePhase}
            selectedFeatureId={selectedFeature?.id ?? null}
            onSelectFeature={handleSelectFeature}
            bulkSelectedIds={canBulkComplete ? bulkSelectedIds : undefined}
            onToggleBulkSelect={canBulkComplete ? handleToggleBulkSelect : undefined}
          />
        </Paper>
      </Box>
      {!isMobile && (
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Kéo để điều chỉnh bề rộng cột tính năng"
          aria-valuenow={sidebarWidth}
          aria-valuemin={SIDEBAR_MIN_WIDTH}
          aria-valuemax={SIDEBAR_MAX_WIDTH}
          tabIndex={0}
          onPointerDown={(event) => {
            event.preventDefault();
            setResizing(true);
          }}
          onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT_WIDTH)}
          onKeyDown={handleResizerKeyDown}
          sx={{
            flex: '0 0 20px',
            alignSelf: 'stretch',
            position: 'sticky',
            top: SIDEBAR_TOP_OFFSET,
            height: `calc(100vh - ${SIDEBAR_TOP_OFFSET}px - 16px)`,
            cursor: 'col-resize',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&::before': {
              content: '""',
              width: 4,
              height: 48,
              borderRadius: 2,
              bgcolor: resizing ? 'primary.main' : 'divider',
              transition: 'background-color .15s',
            },
            '&:hover::before': { bgcolor: 'primary.main' },
            '&:focus-visible': { outline: 'none' },
            '&:focus-visible::before': { bgcolor: 'primary.main', height: 80 },
          }}
        />
      )}
      <Box ref={detailSectionRef} sx={{ flexGrow: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
        <Stack spacing={1.5}>
          <Can
            permission={[
              'PROJECT_DOCUMENT_VIEW',
              'FEATURE_UPDATE',
              'FEATURE_DELETE',
              'FEATURE_IMAGE_VIEW',
            ]}
          >
            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
              <Can permission="PROJECT_DOCUMENT_VIEW">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  onClick={() => navigate(`/projects/${projectId}/documents`)}
                >
                  Tài liệu dự án
                </Button>
              </Can>
              {selectedFeature && (
                <>
                  <Can permission="FEATURE_IMAGE_VIEW">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ImageIcon />}
                      onClick={() => setImagesFeature(selectedFeature)}
                    >
                      Hình ảnh
                    </Button>
                  </Can>
                  <Can permission="FEATURE_UPDATE">
                    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={openEdit}>
                      Sửa
                    </Button>
                  </Can>
                  <Can permission="FEATURE_DELETE">
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteTarget(selectedFeature)}
                    >
                      Xoá
                    </Button>
                  </Can>
                </>
              )}
            </Stack>
          </Can>
          {selectedFeature ? (
            <AcceptanceFeatureDetail
              key={selectedFeature.id}
              featureId={selectedFeature.id}
              projectId={projectId}
              phaseId={currentPhaseId}
              phaseName={currentPhaseName}
              onStatusChanged={handleStatusChanged}
              refreshToken={imagesRefreshToken}
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <InfoOutlinedIcon />
              <Typography>
                {loadingPhases ? 'Đang tải dữ liệu…' : 'Chọn một tính năng bên trái để xem chi tiết nghiệm thu.'}
              </Typography>
            </Paper>
          )}
        </Stack>
      </Box>

      {/* Dialog Thêm / Sửa tính năng */}
      {managePhaseId && (
        <FeatureFormDialog
          open={formOpen}
          mode={formMode}
          phaseId={managePhaseId}
          feature={formMode === 'edit' ? selectedFeature : null}
          defaultOrderIndex={featuresByPhase[managePhaseId]?.length ?? 0}
          onClose={() => setFormOpen(false)}
          onSaved={() => void refreshPhase(managePhaseId)}
        />
      )}

      {/* Dialog Import hàng loạt */}
      {managePhaseId && (
        <FeatureImportDialog
          open={importOpen}
          phaseId={managePhaseId}
          onClose={() => setImportOpen(false)}
          onImported={() => void refreshPhase(managePhaseId)}
        />
      )}

      {/* Dialog quản lý hình ảnh */}
      <FeatureImagesDialog
        feature={imagesFeature}
        onClose={() => setImagesFeature(null)}
        onImagesChanged={() => setImagesRefreshToken((v) => v + 1)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm tính năng"
        message={`Tính năng "${deleteTarget?.name}" sẽ bị ẩn khỏi hệ thống (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDialogOpen}
        title="Đánh dấu hoàn thành hàng loạt"
        message={`Đánh dấu ${bulkSelectedIds.size} tính năng đã chọn là "Hoàn thành"?`}
        confirmLabel="Xác nhận"
        confirmColor="primary"
        loading={bulkCompleting}
        onConfirm={() => void handleBulkComplete()}
        onCancel={() => setBulkDialogOpen(false)}
      />
    </Box>
  );
}
